import { colors } from '../../../theme/tokens/colors';
import { chat as defaultAccent, status as statusTokens } from '../../../theme/tokens/semantic';
import { radius, radiusPx } from '../../../theme/tokens/radius';
import { shadows as defaultShadows } from '../../../theme/tokens/shadows';

const PAGE_MIN_HEIGHT = 'calc(100vh - 80px)';

export function createLicenseSx(tokenOverrides = {}) {
  const accent = tokenOverrides.accent ?? defaultAccent;
  const shadows = tokenOverrides.shadows ?? defaultShadows;

  const controlRadius = radiusPx.md;

  const pageSearchInputRoot = {
    borderRadius: controlRadius,
    fontSize: '0.875rem',
    '& fieldset': { borderColor: accent.divider ?? colors.grey[300] },
    '&:hover fieldset': { borderColor: colors.grey[400] },
    '&.Mui-focused fieldset': {
      borderColor: accent.primary,
      borderWidth: 1,
    },
  };

  return {
    pageRoot: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: PAGE_MIN_HEIGHT,
      bgcolor: colors.grey[50],
      px: { xs: 2, md: 3 },
      py: 2.5,
    },
    pageHeader: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
      mb: 3,
    },
    pageTitle: {
      fontWeight: 700,
      color: accent.textPrimary ?? colors.grey[900],
    },
    pageSubtitle: {
      color: accent.textSecondary ?? colors.grey[700],
      mt: 0.5,
    },
    headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      flexWrap: 'wrap',
    },
    searchField: {
      minWidth: { xs: '100%', sm: 280 },
      '& .MuiOutlinedInput-root': {
        ...pageSearchInputRoot,
        bgcolor: colors.white,
      },
    },
    createButton: {
      textTransform: 'none',
      fontWeight: 600,
      borderRadius: controlRadius,
      px: 2.5,
      bgcolor: accent.primary,
      boxShadow: 'none',
      '&:hover': {
        bgcolor: accent.primaryDark,
        boxShadow: shadows.md,
      },
    },
    statusTabs: {
      mb: 2.5,
      borderBottom: `1px solid ${colors.grey[200]}`,
      bgcolor: colors.white,
      borderRadius: `${radius.lg}px ${radius.lg}px 0 0`,
      px: { xs: 1, md: 2 },
      '& .MuiTabs-indicator': {
        height: 3,
        borderRadius: '3px 3px 0 0',
        bgcolor: accent.primary,
      },
    },
    statusTab: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.875rem',
      minHeight: 48,
      color: accent.textSecondary ?? colors.grey[600],
      '&.Mui-selected': {
        color: accent.primary,
      },
    },
    statusTabCount: {
      ml: 0.75,
      px: 0.75,
      py: 0.125,
      borderRadius: `${radius.pill}px`,
      bgcolor: colors.grey[100],
      color: accent.textSecondary ?? colors.grey[700],
      fontSize: '0.75rem',
      fontWeight: 700,
      lineHeight: 1.4,
    },
    statusTabCountSelected: {
      bgcolor: accent.primaryMuted,
      color: accent.primary,
    },
    tabPanel: {
      bgcolor: colors.white,
      borderRadius: `0 0 ${radius.lg}px ${radius.lg}px`,
      border: `1px solid ${colors.grey[200]}`,
      borderTop: 'none',
      p: { xs: 2, md: 2.5 },
    },
    cardGrid: {
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, 1fr)',
        lg: 'repeat(3, 1fr)',
      },
      gap: 2.5,
    },
    card: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      borderRadius: `${radius.lg}px`,
      border: `1px solid ${accent.primaryBorder ?? colors.grey[300]}`,
      bgcolor: colors.white,
      boxShadow: shadows.sm,
      transition: 'box-shadow 0.2s ease, transform 0.2s ease',
      '&:hover': {
        boxShadow: shadows.md,
        transform: 'translateY(-2px)',
      },
    },
    cardHeader: {
      px: 2.5,
      pt: 2.5,
      pb: 1.5,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 1,
    },
    cardBody: {
      px: 2.5,
      pb: 2,
      flex: 1,
    },
    cardFooter: {
      px: 2.5,
      py: 1.75,
      borderTop: `1px solid ${colors.grey[200]}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
    },
    cardActions: {
      px: 2.5,
      pb: 2,
      pt: 0.5,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 1,
      borderTop: `1px solid ${colors.grey[200]}`,
    },
    cardActionButton: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.8125rem',
      minWidth: 0,
      px: 1.25,
    },
    statusChip: {
      fontWeight: 600,
      fontSize: '0.7rem',
      height: 24,
      borderRadius: `${radius.pill}px`,
    },
    statusActive: {
      bgcolor: '#ECFDF3',
      color: '#027A48',
    },
    statusExpiring: {
      bgcolor: statusTokens.warningSurface,
      color: statusTokens.warningText,
    },
    statusExpired: {
      bgcolor: statusTokens.expiredBg,
      color: statusTokens.expired,
    },
    metaRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      color: accent.textMuted ?? colors.grey[500],
      fontSize: '0.8125rem',
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      py: 8,
      px: 3,
      borderRadius: `${radius.xl}px`,
      border: `1px dashed ${accent.primaryBorder ?? colors.grey[300]}`,
      bgcolor: colors.white,
    },
    emptyIcon: {
      width: 64,
      height: 64,
      borderRadius: radius.full,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: accent.primaryMuted,
      color: accent.primary,
      mb: 2,
    },
    skeletonCard: {
      borderRadius: `${radius.lg}px`,
      height: 220,
    },
    dialog: {
      formStack: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
      },
      alert: {
        borderRadius: `${radius.md}px`,
        mb: 0,
      },
      error: {
        color: statusTokens.expired,
        fontSize: '0.75rem',
        mt: 0.5,
      },
    },
    optionPicker: {
      root: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
      },
      card: {
        flex: '1 1 0',
        minWidth: 90,
        py: 1.25,
        px: 1.5,
        borderRadius: controlRadius,
        border: `1px solid ${colors.grey[300]}`,
        bgcolor: colors.white,
        cursor: 'pointer',
        textAlign: 'center',
        transition: 'all 0.15s ease',
        '&:hover': {
          borderColor: accent.primary,
          bgcolor: accent.primaryMuted,
        },
      },
      cardSelected: {
        borderColor: accent.primary,
        bgcolor: accent.primaryMuted,
        boxShadow: `0 0 0 1px ${accent.primary}`,
      },
      chip: {
        borderRadius: `${radius.pill}px`,
        px: 2,
        py: 0.75,
        border: `1px solid ${colors.grey[300]}`,
        bgcolor: colors.white,
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '0.8125rem',
        transition: 'all 0.15s ease',
        '&:hover': {
          borderColor: accent.primary,
          bgcolor: accent.primaryMuted,
        },
      },
      chipSelected: {
        borderColor: accent.primary,
        bgcolor: accent.primary,
        color: colors.white,
      },
    },
  };
}
